import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { useExperiment } from "@hopper-b2b/experiments";
import { useI18nContext } from "@hopper-b2b/i18n";
import { availabilitySelectors } from "@hopper-b2b/lodging";
import { ButtonWrap } from "@hopper-b2b/ui";
import {
  getGoogleStaticMapSrc,
  STATIC_MAP_DARK_STYLE,
  useVisibility,
} from "@hopper-b2b/utilities";

import { EXPERIMENTS } from "../../../App";
import ChevronDarkIcon from "../../../assets/client/chevron.svg?react";
import ChevronWhiteIcon from "../../../assets/client/darkMode/chevron-white.svg?react";
import { useDarkModePreferred } from "../../../utils/colors";
import style from "./styles.module.scss";

const getWidth = () =>
  Math.max(document.documentElement.clientWidth, window.innerWidth || 0) - 32; //vw - 2rem(padding)

export const HotelListMapCard = ({ onClick }: { onClick: () => void }) => {
  const { t } = useI18nContext();
  const isDarkMode = useDarkModePreferred();
  const centroid = useSelector(availabilitySelectors.getCentroid);
  const hideHotelMapCardExperiment = useExperiment(
    EXPERIMENTS.RENDER_HOTEL_MAP_ENTRY_POINT
  );

  const ChevronIcon = isDarkMode ? ChevronWhiteIcon : ChevronDarkIcon;

  const [visible, ref] = useVisibility<HTMLButtonElement>(0, 20);
  const hasVisibilityRun = useRef(false);

  const setToggleVisibility = useCallback((visibility: "" | "hidden") => {
    const element = document.querySelector<HTMLElement>(
      ".Availability-view-toggle"
    );
    if (element) {
      element.style.visibility = visibility;
      return true;
    }
    return false;
  }, []);

  useLayoutEffect(() => {
    if (hideHotelMapCardExperiment) return;
    if (setToggleVisibility("hidden")) {
      return;
    }
    const observer = new MutationObserver(() => {
      if (setToggleVisibility("hidden")) {
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [hideHotelMapCardExperiment, setToggleVisibility]);

  useEffect(() => {
    if (hideHotelMapCardExperiment) return;
    if (!hasVisibilityRun.current) {
      hasVisibilityRun.current = true;
      // Keep hidden on first run; we'll reveal after the next visibility change.
      setToggleVisibility("hidden");
      return;
    }
    setToggleVisibility(visible ? "hidden" : "");
  }, [hideHotelMapCardExperiment, setToggleVisibility, visible]);

  useEffect(() => {
    return () => {
      hasVisibilityRun.current = false;
    };
  }, []);

  useEffect(() => {
    if (hideHotelMapCardExperiment) return;
    return () => {
      setToggleVisibility("");
    };
  }, [hideHotelMapCardExperiment, setToggleVisibility]);

  return centroid?.lat && centroid?.lng && !hideHotelMapCardExperiment ? (
    <ButtonWrap
      onClick={onClick}
      className="nubank-hotel-list-map-card"
      ref={ref}
    >
      <img
        className={style.staticMapImage}
        src={getGoogleStaticMapSrc(
          `${centroid.lat},${centroid.lng}`,
          "11", // zoom to show the city name as much as possible
          `${getWidth()}x84`, //fixed height of 84px to match designs
          "1", //scale
          false,
          "red",
          isDarkMode ? STATIC_MAP_DARK_STYLE : ""
        )}
        alt={t("hotelMapCardAlt")}
      />
      <div className={style.hotelMapCardBottom}>
        <p className={style.label}>{t("hotelMapCardLabel")}</p>
        <ChevronIcon
          className={!isDarkMode ? style.darkIcon : style.lightIcon}
        />
      </div>
    </ButtonWrap>
  ) : null;
};
